import { useCallback, useContext, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { unstable_batchedUpdates } from 'react-dom';
import _ from 'lodash';
import { eadmissionAPI } from '../../../apis';
import { ADMISSION_AED_VIEW_LOCATION_FILTER } from '../../../comm/constant/storage';
import useUpdateEffect from '../../../utils/useUpdateEffect';
import AdmissionLocationView from '../components/AdmissionLocationView';
import AdmissionLocationFilter from '../components/AdmissionLocationFilter';
import StatisticDetail from '../components/AdmissionLocationView/StatisticDetail';
import {
  buildLocationCubicleUpdateParams,
  revampLocationCubicle,
  revampLocationPatient,
} from '../components/AdmissionLocationView/utils';
import { AdmissionContext } from '..';

const initStatisticData = () => ({
  admDecTime: null,
  pendingCount: null,
  progressCount: null,
  admStatic: {
    maleCount: null,
    femaleCount: null,
    priority1: null,
    total: null,
  },
  admSpecStatic: [],
});

// 初始化AED View的filter参数。
const initLocationFilter = () => ({
  aeNo: '',
  busstops: [],
  sex: '',
  specialtys: [],
  waitTime: 0,
});

// 读取AED View filter缓存，缓存异常时使用默认条件。
const getStoredLocationFilter = () => {
  try {
    const storageFilter = JSON.parse(localStorage.getItem(ADMISSION_AED_VIEW_LOCATION_FILTER));
    if (!storageFilter) {
      return initLocationFilter();
    }

    return {
      ...initLocationFilter(),
      ...storageFilter,
      busstops: storageFilter.busstops ?? [],
      specialtys: storageFilter.specialtys ?? [],
    };
  } catch (error) {
    return initLocationFilter();
  }
};

// 组装AED View列表查询参数。
const buildAEDCubicleDetailParams = (filter) => ({
  filter: {
    ...initLocationFilter(),
    ...filter,
  },
});

// AED View的Statistic沿用E-Admission Pending的统计参数。
const buildAEDStatisticParams = (tabValues) => ({
  pageSize: null,
  pageNo: null,
  type: tabValues.Pending,
  sort: {
    column: '',
    order: '',
  },
  filter: {
    actionWardId: 0,
    aeNo: '',
    chiName: '',
    diagnosis: '',
    engName: '',
    handingByIds: [],
    priority: [],
    remark: '',
    sex: '',
    specialtys: [],
    status: [],
    hospCode: '',
    startDate: null,
    endDate: null,
    waitTime: '0',
    busstops: [],
  },
});

const AEDView = ({ headerRef, contentHeight }) => {
  const history = useHistory();
  const { admissionParamsRef, tabValues, specialtyList, busstopList, showAEDDetail, setShowAEDDetail } =
    useContext(AdmissionContext);
  const [queuebarList, setQueuebarList] = useState([]);
  const [cubicleList, setCubicleList] = useState([]);
  const [locationFilter, setLocationFilter] = useState(getStoredLocationFilter);
  const [statisticData, setStatisticData] = useState(initStatisticData);
  const [refresh, setRefresh] = useState(false);
  const fullScreen = useSelector((state) => state.fullScreenReducer);
  const loginInfo = useSelector((state) => state.loginInfo);

  const revampPatient = useCallback(
    async (patient) => {
      return revampLocationPatient({ patient, isLogin: loginInfo.flag });
    },
    [loginInfo.flag]
  );

  const revampCubicle = useCallback(
    async (cubicle) => {
      return revampLocationCubicle({ cubicle, revampPatient });
    },
    [revampPatient]
  );

  const getCubicleDetail = useCallback(async () => {
    try {
      const { result } = await eadmissionAPI.getAEDCubicleDetail(buildAEDCubicleDetailParams(locationFilter));
      const [newPatientList, newCubicleList] = await Promise.all([
        Promise.all(_.map(result?.queueBar, (item) => revampPatient(item))),
        Promise.all(_.map(result?.cubicles, (item) => revampCubicle(item))),
      ]);
      unstable_batchedUpdates(() => {
        setQueuebarList(newPatientList);
        setCubicleList(newCubicleList);
      });
    } catch (error) {}
  }, [revampPatient, revampCubicle, locationFilter]);

  const getStatisticDetail = useCallback(async () => {
    try {
      const { result } = await eadmissionAPI.getAdmissionCount(buildAEDStatisticParams(tabValues));
      setStatisticData((prev) => ({
        ...prev,
        ...result,
      }));
    } catch (error) {}
  }, [tabValues]);

  const statisticNode = useMemo(() => <StatisticDetail data={statisticData} />, [statisticData]);

  // 条码扫描后直接同步AED View筛选条件。
  const handleLocationAeNoBarcodeScan = useCallback((value) => {
    setLocationFilter((preFilter) => ({
      ...preFilter,
      aeNo: value,
    }));
  }, []);

  // AED View的filter只在Location View内部使用。
  const locationFilterNode = useMemo(
    () => (
      <AdmissionLocationFilter
        statisticData={statisticData}
        filter={locationFilter}
        onFilterChange={setLocationFilter}
        specialtyList={specialtyList}
        busstopList={busstopList}
        onAeNoBarcodeScan={handleLocationAeNoBarcodeScan}
        onReset={() => setLocationFilter(initLocationFilter())}
        showDetails={showAEDDetail}
        onShowDetailsChange={setShowAEDDetail}
      />
    ),
    [locationFilter, specialtyList, busstopList, handleLocationAeNoBarcodeScan, statisticData, showAEDDetail]
  );

  // 保存AED View filter，刷新后保留筛选条件。
  useEffect(() => {
    localStorage.setItem(ADMISSION_AED_VIEW_LOCATION_FILTER, JSON.stringify(locationFilter));
  }, [locationFilter]);

  // 将病人从cubicle中的病床中移除
  const handleDeleteBedPatient = useCallback(
    async ({ cubicleId, bedData, cubicleVersion, setSelectedBed }) => {
      try {
        const params = {
          cubicleId,
          eaId: bedData?.patientInfo?.eaId,
          version: cubicleVersion,
        };
        await eadmissionAPI.assignOrDelPatient(params);
        await getCubicleDetail();
        setSelectedBed(null);
      } catch (error) {}
    },
    [getCubicleDetail]
  );

  // 绑定病人到病床
  const handleAssignPatient = useCallback(
    async ({ selectedPatient, selectedBed, setSelectedPatient, setSelectedBed }) => {
      try {
        if (!selectedPatient || !selectedBed || selectedBed?.hasPatient) {
          return;
        }
        const params = {
          column: selectedBed?.column,
          cubicleId: selectedBed?.cubicleId,
          eaId: selectedPatient?.eaId,
          row: selectedBed?.row,
          version: selectedBed?.cubicleVersion,
        };
        await eadmissionAPI.assignOrDelPatient(params);
        await getCubicleDetail();
        setSelectedPatient(null);
        setSelectedBed(null);
      } catch (error) {
        setSelectedPatient(null);
        setSelectedBed(null);
      }
    },
    [getCubicleDetail]
  );

  const handleUpdateCubicle = useCallback(
    async (cubicleData) => {
      try {
        const params = buildLocationCubicleUpdateParams(cubicleData);
        await eadmissionAPI.updateLandingCubicle(params);
        await getCubicleDetail();
      } catch (error) {}
    },
    [getCubicleDetail]
  );

  const handleOpenPatientDetail = useCallback(
    ({ patientInfo }) => {
      if (!patientInfo?.eaId) {
        return;
      }

      history.push(`/admissionForm/${patientInfo.eaId}`);
    },
    [history]
  );

  useImperativeHandle(admissionParamsRef, () => ({
    setRefresh,
  }));

  // 切换用户角色或ward时重新加载cubicle资料。
  useEffect(() => {
    getCubicleDetail();
    getStatisticDetail();
  }, [getCubicleDetail, getStatisticDetail, refresh, loginInfo]);

  useUpdateEffect(() => {
    setRefresh((o) => !o);

    if (fullScreen) {
      const interval = setInterval(() => {
        setRefresh((o) => !o);
      }, 1000 * 60 * 5);

      return () => clearInterval(interval);
    }
  }, [fullScreen]);

  return (
    <AdmissionLocationView
      headerRef={headerRef}
      contentHeight={contentHeight}
      filterNode={locationFilterNode}
      statisticNode={statisticNode}
      queuebarList={queuebarList}
      cubicleList={cubicleList}
      handleAssignPatient={handleAssignPatient}
      handleDeleteBedPatient={handleDeleteBedPatient}
      handleOpenPatientDetail={handleOpenPatientDetail}
      handleUpdateCubicle={handleUpdateCubicle}
      queuebarStorageKey="SHOW_AED_VIEW_QUEUE_BAR"
      floorplanZoomStorageKey="AED_VIEW_FLOORPLAN_ZOOM"
      viewType="AED"
      showDetails={showAEDDetail}
      patientInfoViewType="AED"
    />
  );
};

export default AEDView;
